<template>
    <div class="animated fadeIn">
        <b-row>
            <b-col cols="12" md="12">
                <component-header
                    title="Taxcode Master Config"
                    @search-query="(val) => (filter = val)"
                    :hasClear="false"
                    @reload-data="reloadData"
                    :hasReload="true"
                    :isLoading="isloading"
                    @create-press="$router.push({ name: 'CreateTaxCode' })"
                    createButtonText="Create Taxcode"
                />
            </b-col>
        </b-row>
        <b-card class="card-wrap">
            <div class="mt-2">
                <b-table
                    class="table-radius mt-3"
                    :dark="dark"
                    :hover="hover"
                    :striped="striped"
                    :bordered="bordered"
                    :small="small"
                    :fixed="fixed"
                    responsive
                    id="my-table"
                    :busy="isloading"
                    :current-page="currentPage"
                    :per-page="perPage"
                    :items="taxcodes"
                    thead-class="header"
                    :fields="Columns"
                    show-empty
                    :filter="filter"
                    @filtered="onFilter"
                    :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc"
                >
                    <template slot="cell(action)" slot-scope="data">
                        <div class="inline_btnTable text-center">
                            <ActionButtonGroup
                                showView
                                showEdit
                                showDelete
                                @view="navigateTo(data, 'view')"
                                @edit="navigateTo(data, 'edit')"
                                @delete="deleteTaxcode(data)"
                            />
                        </div>
                    </template>
                    <template v-slot:empty="scope">
                        <h4>{{ scope.emptyText }}</h4>
                    </template>
                </b-table>
            </div>
            <div
                class="d-flex justify-content-end pagination-wrapper"
                v-if="taxcodes.length > perPage"
            >
                <b-pagination
                    v-model="currentPage"
                    :per-page="perPage"
                    :total-rows="filterCount"
                    aria-controls="my-table"
                    prev-text="Prev"
                    next-text="Next"
                    hide-goto-end-buttons
                ></b-pagination>
            </div>
        </b-card>
    </div>
</template>

<script>
import ComponentHeader from "@/components/componentNewHeader";
import moment from "moment";
import ActionButtonGroup from "@/components/Common/ActionButtonGroup.vue";

export default {
    name: "TaxCodedetails",
    components: {
        ComponentHeader,
        ActionButtonGroup,
    },
    data() {
        return {
            filterCount: 0,
            sortBy: "createdAt",
            sortDesc: true,
            isloading: false,
            filter: "",
            perPage: 10,
            hover: true,
            striped: false,
            bordered: true,
            small: false,
            dark: false,
            fixed: false,
            currentPage: 1,
            Columns: [
                {
                    key: "Tax Code",
                    sortable: true,
                },
                {
                    key: "Country",
                    sortable: true,
                },
                {
                    key: "Start Date",
                },
                {
                    key: "End Date",
                },
                {
                    key: "action",
                    class: "text-center",
                },
            ],
        };
    },
    computed: {
        taxcodes() {
            let data = this.$store.getters["taxcode/GetTaxcodeList"];

            if (Array.isArray(data) && data.length > 0) {
                return data.map((d) => ({
                    Country: d.country.toUpperCase(),
                    "Start Date": moment(d.startDate).format("DD/MM/yyyy"),
                    "End Date": moment(d.endDate).format("DD/MM/yyyy"),
                    "Tax Code": d.taxCode,
                    _id: d._id,
                    restData: d,
                }));
            }
            return [];
        },
    },
    watch: {
        taxcodes: {
            handler(newVal) {
                this.$nextTick(() => {
                    // If there's an active filter, b-table will emit @filtered event to update filterCount
                    if (!this.filter || this.filter.trim() === "") {
                        this.filterCount = newVal.length;
                    }
                    // If filter exists, b-table will automatically emit @filtered event
                    // which will update filterCount via onFilter method
                });
            },
            immediate: true,
        },
    },
    methods: {
        /**
         * Centralized method to fetch taxcode list from API
         * @param {Object} options - Configuration options
         * @param {Boolean} options.showLoading - Whether to show loading state (default: false)
         * @param {Boolean} options.resetPage - Whether to reset to page 1 (default: false)
         * @param {Function} options.onSuccess - Callback function on success
         * @param {Function} options.onError - Callback function on error
         */
        async fetchTaxcodeList(options = {}) {
            const {
                showLoading = false,
                resetPage = false,
                onSuccess,
                onError,
            } = options;

            if (showLoading) {
                this.isloading = true;
            }

            try {
                const userId = this.$store.getters["auth/getUserID"];
                await this.$store.dispatch(
                    "taxcode/fetchTaxcodeListforTenant",
                    userId,
                );

                // filterCount will be synchronized via watcher and @filtered event
                if (resetPage) {
                    this.currentPage = 1;
                }

                if (onSuccess && typeof onSuccess === "function") {
                    onSuccess();
                }
            } catch (error) {
                console.error("Error fetching taxcode list:", error);
                if (onError && typeof onError === "function") {
                    onError(error);
                } else {
                    this.$toasted.error(error.message || "Failed to fetch taxcode list");
                }
            } finally {
                if (showLoading) {
                    this.isloading = false;
                }
            }
        },
        getTaxCodeData() {
            this.fetchTaxcodeList();
        },
        onFilter(list) {
            this.filterCount = list.length;
            this.currentPage = 1;
        },
        navigateTo(data, type) {
            let obj = {
                country: data.item["Country"],
                startDate: data.item["Start Date"],
                endDate: data.item["End Date"],
                taxCode: data.item["Tax Code"],
                restData: data.item["restData"],
            };
            if (type === "edit") {
                this.$router.push({
                    name: "EditTaxCode",
                    params: { data: obj },
                });
                return;
            }
            if (type === "view") {
                this.$router.push({
                    name: "ViewTaxCode",
                    params: { data: obj },
                });
                return;
            }
        },
        async deleteTaxcode(data) {
            this.$swal({
                title: "Delete this Taxcode?",
                text: "Are you sure? You won't be able to revert this!",
                type: "warning",
                showCancelButton: true,
                confirmButtonColor: "#3085d6",
                confirmButtonText: "Yes, Delete it!",
                cancelButtonColor: "#d33",
                reverseButtons: true,
            }).then(async (result) => {
                if (result.value) {
                    try {
                        await this.$store.dispatch(
                            "taxcode/deleteTaxcode",
                            data.item._id,
                        );
                        this.$toasted.success("Taxcode Deleted Successfully");
                        // Refresh the list after deletion
                        this.fetchTaxcodeList({
                            onError: (error) => {
                                this.$toasted.error(error.message);
                            },
                        });
                    } catch (error) {
                        this.$toasted.error(
                            error.message || "Failed to delete taxcode",
                        );
                    }
                }
            });
        },
        async reloadData() {
            await this.fetchTaxcodeList({
                showLoading: true,
                resetPage: true,
            });
        },
    },
    async mounted() {
        this.reloadData();
    },
};
</script>

<style scoped>
.table-radius {
    border-radius: 12px;
}

.btn-edit-taxcode {
    background: #fe7b08;
    border-color: #fe7b08;
}

.inline_btnTable {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.inline_btnTable .button-circle {
    flex-shrink: 0;
}

.pagination-wrapper {
    margin-top: 1rem;
}

::v-deep .pagination {
    justify-content: flex-end;
}

@media (max-width: 767.98px) {
    .pagination-wrapper {
        justify-content: center;
    }
}
</style>
